// src/components/CV/CVForm.jsx
import React from 'react';

const CVForm = ({ cvData, handlers }) => {
    const {
        updatePersonal,
        updateSummary,
        updateSkills,
        updateCertifications,
        updateLanguages,
        education,
        addEducation,
        updateEducation,
        removeEducation,
        experience,
        addExperience,
        updateExperience,
        removeExperience
    } = handlers;

    const { personal } = cvData;

    // Split skills and languages for preview
    const skillsArray = cvData.skills ? cvData.skills.split(',').map(s => s.trim()).filter(Boolean) : [];
    const languagesArray = cvData.languages ? cvData.languages.split(',').map(s => s.trim()).filter(Boolean) : [];

    return (
        <div className="space-y-6">
            {/* Personal Information */}
            <div className="bg-white rounded-xl p-5 shadow-md">
                <h3 className="font-bold text-gradpath-dark text-lg mb-4 flex items-center gap-2">
                    <i className="fas fa-user text-gradpath-green"></i> Personal Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                    <input
                        type="text"
                        placeholder="Full Name"
                        value={personal.name}
                        onChange={(e) => updatePersonal('name', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green focus:ring-1 focus:ring-gradpath-green outline-none"
                    />
                    <input
                        type="text"
                        placeholder="Professional Title"
                        value={personal.title}
                        onChange={(e) => updatePersonal('title', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green focus:ring-1 focus:ring-gradpath-green outline-none"
                    />
                    <input
                        type="email"
                        placeholder="Email"
                        value={personal.email}
                        onChange={(e) => updatePersonal('email', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green focus:ring-1 focus:ring-gradpath-green outline-none"
                    />
                    <input
                        type="tel"
                        placeholder="Phone"
                        value={personal.phone}
                        onChange={(e) => updatePersonal('phone', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green focus:ring-1 focus:ring-gradpath-green outline-none"
                    />
                    <input
                        type="text"
                        placeholder="Location"
                        value={personal.location}
                        onChange={(e) => updatePersonal('location', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green focus:ring-1 focus:ring-gradpath-green outline-none"
                    />
                    <input
                        type="text"
                        placeholder="LinkedIn (optional)"
                        value={personal.linkedin}
                        onChange={(e) => updatePersonal('linkedin', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green focus:ring-1 focus:ring-gradpath-green outline-none"
                    />
                </div>
            </div>

            {/* Professional Summary */}
            <div className="bg-white rounded-xl p-5 shadow-md">
                <h3 className="font-bold text-gradpath-dark text-lg mb-4 flex items-center gap-2">
                    <i className="fas fa-file-alt text-gradpath-green"></i> Professional Summary
                </h3>
                <textarea
                    placeholder="Write a brief professional summary..."
                    value={cvData.summary}
                    onChange={(e) => updateSummary(e.target.value)}
                    rows="4"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green focus:ring-1 focus:ring-gradpath-green outline-none"
                />
            </div>

            {/* Education Section */}
            <div className="bg-white rounded-xl p-5 shadow-md">
                <h3 className="font-bold text-gradpath-dark text-lg mb-4 flex items-center gap-2">
                    <i className="fas fa-graduation-cap text-gradpath-green"></i> Education
                </h3>
                {education.map((edu) => (
                    <div key={edu.id} className="border border-gray-200 rounded-lg p-4 mb-4 relative">
                        <button
                            onClick={() => removeEducation(edu.id)}
                            className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                        >
                            <i className="fas fa-trash"></i>
                        </button>
                        <div className="grid md:grid-cols-2 gap-3">
                            <input
                                type="text"
                                placeholder="Degree/Qualification"
                                value={edu.degree}
                                onChange={(e) => updateEducation(edu.id, 'degree', e.target.value)}
                                className="p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                            />
                            <input
                                type="text"
                                placeholder="Institution"
                                value={edu.institution}
                                onChange={(e) => updateEducation(edu.id, 'institution', e.target.value)}
                                className="p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                            />
                            <input
                                type="text"
                                placeholder="Year"
                                value={edu.year}
                                onChange={(e) => updateEducation(edu.id, 'year', e.target.value)}
                                className="p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                            />
                            <input
                                type="text"
                                placeholder="Grade/Achievement"
                                value={edu.grade}
                                onChange={(e) => updateEducation(edu.id, 'grade', e.target.value)}
                                className="p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                            />
                        </div>
                    </div>
                ))}
                <button
                    onClick={addEducation}
                    className="w-full py-2 border-2 border-dashed border-gradpath-green text-gradpath-green rounded-lg hover:bg-gradpath-green hover:text-white transition-all flex items-center justify-center gap-2"
                >
                    <i className="fas fa-plus"></i> Add Education
                </button>
            </div>

            {/* Work Experience */}
            <div className="bg-white rounded-xl p-5 shadow-md">
                <h3 className="font-bold text-gradpath-dark text-lg mb-4 flex items-center gap-2">
                    <i className="fas fa-briefcase text-gradpath-green"></i> Work Experience
                </h3>
                {experience.map((exp) => (
                    <div key={exp.id} className="border border-gray-200 rounded-lg p-4 mb-4 relative">
                        <button
                            onClick={() => removeExperience(exp.id)}
                            className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                        >
                            <i className="fas fa-trash"></i>
                        </button>
                        <div className="space-y-3">
                            <div className="grid md:grid-cols-2 gap-3">
                                <input
                                    type="text"
                                    placeholder="Job Title"
                                    value={exp.title}
                                    onChange={(e) => updateExperience(exp.id, 'title', e.target.value)}
                                    className="p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                                <input
                                    type="text"
                                    placeholder="Company"
                                    value={exp.company}
                                    onChange={(e) => updateExperience(exp.id, 'company', e.target.value)}
                                    className="p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                                <input
                                    type="text"
                                    placeholder="Years"
                                    value={exp.years}
                                    onChange={(e) => updateExperience(exp.id, 'years', e.target.value)}
                                    className="p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                />
                            </div>
                            <textarea
                                placeholder="Key responsibilities and achievements"
                                value={exp.responsibilities}
                                onChange={(e) => updateExperience(exp.id, 'responsibilities', e.target.value)}
                                rows="3"
                                className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                            />
                        </div>
                    </div>
                ))}
                <button
                    onClick={addExperience}
                    className="w-full py-2 border-2 border-dashed border-gradpath-green text-gradpath-green rounded-lg hover:bg-gradpath-green hover:text-white transition-all flex items-center justify-center gap-2"
                >
                    <i className="fas fa-plus"></i> Add Experience
                </button>
            </div>

            {/* Skills - Updated with bullet point preview */}
            <div className="bg-white rounded-xl p-5 shadow-md">
                <h3 className="font-bold text-gradpath-dark text-lg mb-4 flex items-center gap-2">
                    <i className="fas fa-code text-gradpath-green"></i> Skills
                </h3>
                <textarea
                    placeholder="List your skills (comma separated)&#10;Example: JavaScript, React, Python, SQL"
                    value={cvData.skills}
                    onChange={(e) => updateSkills(e.target.value)}
                    rows="4"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green focus:ring-1 focus:ring-gradpath-green outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">Separate skills with commas (e.g., JavaScript, Python, React)</p>
                {skillsArray.length > 0 && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Preview:</p>
                        <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
                            {skillsArray.slice(0, 5).map((skill, i) => (
                                <li key={i}>{skill}</li>
                            ))}
                            {skillsArray.length > 5 && (
                                <li className="text-gray-400 text-xs">+{skillsArray.length - 5} more</li>
                            )}
                        </ul>
                    </div>
                )}
            </div>

            {/* Certifications */}
            <div className="bg-white rounded-xl p-5 shadow-md">
                <h3 className="font-bold text-gradpath-dark text-lg mb-4 flex items-center gap-2">
                    <i className="fas fa-certificate text-gradpath-green"></i> Certifications & Awards
                </h3>
                <textarea
                    placeholder="List certifications and awards (one per line)"
                    value={cvData.certifications}
                    onChange={(e) => updateCertifications(e.target.value)}
                    rows="4"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green focus:ring-1 focus:ring-gradpath-green outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">Separate items with new lines</p>
            </div>

            {/* Languages - Updated with bullet point preview */}
            <div className="bg-white rounded-xl p-5 shadow-md">
                <h3 className="font-bold text-gradpath-dark text-lg mb-4 flex items-center gap-2">
                    <i className="fas fa-language text-gradpath-green"></i> Languages
                </h3>
                <textarea
                    placeholder="List your languages (comma separated)&#10;Example: English (Native), Zulu (Fluent), Afrikaans (Intermediate)"
                    value={cvData.languages}
                    onChange={(e) => updateLanguages(e.target.value)}
                    rows="3"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green focus:ring-1 focus:ring-gradpath-green outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">Separate languages with commas</p>
                {languagesArray.length > 0 && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Preview:</p>
                        <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
                            {languagesArray.map((lang, i) => (
                                <li key={i}>{lang}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CVForm;